import { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TrendingUp, Wallet, Building2 } from "lucide-react";
import { analyticsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatFCFA, BUCKET_META } from "@/lib/crmHelpers";

const BUCKET_ORDER = ["hot", "warm", "nurture", "pending", "won", "lost"];
const BUCKET_COLORS = {
  hot: "#ef4444",
  warm: "#f59e0b",
  nurture: "#10b981",
  pending: "#3b82f6",
  won: "#8b5cf6",
  lost: "#71717a",
};

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  // Surface the underlying data point so the tooltip's count truly matches
  // the bar the user is hovering. Recharts sometimes passes a stale payload
  // when bars are drawn with 0 height; guarding against undefined makes the
  // displayed value the same as the bar's actual y-value.
  const dataPoint = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-border/60 bg-card px-3 py-2 text-xs shadow-lg">
      <div className="font-heading font-semibold">
        {label}
        {dataPoint.start && (
          <span className="ml-1 text-[10px] font-normal text-muted-foreground">
            (sem. du {dataPoint.start})
          </span>
        )}
      </div>
      {payload.map((p) => (
        <div key={p.name} className="mt-0.5">
          <span style={{ color: p.fill || p.color }}>●</span>{" "}
          <span className="text-muted-foreground">{p.name} :</span>{" "}
          <span className="font-semibold">
            {typeof p.value === "number" ? p.value : 0}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsSection({ stats }) {
  const { activeProjectId } = useSettings();
  const [weekly, setWeekly] = useState([]);
  const [ca, setCa] = useState(null);
  const [byProgramme, setByProgramme] = useState([]);

  const load = useCallback(async () => {
    try {
      const [w, c, p] = await Promise.all([
        analyticsApi.weekly(8, activeProjectId),
        analyticsApi.caPotentiel(activeProjectId),
        analyticsApi.conversionByProgramme(activeProjectId),
      ]);
      setWeekly(w || []);
      setCa(c || null);
      setByProgramme(p || []);
    } catch (e) {
      /* ignore */
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = stats?.buckets || {};
  const pieData = BUCKET_ORDER.map((k) => ({
    name: BUCKET_META[k].label,
    value: buckets[k] || 0,
    key: k,
  })).filter((d) => d.value > 0);

  return (
    <section data-testid="analytics-section" className="space-y-5">
      <div>
        <div className="text-label text-muted-foreground">Analytique</div>
        <h2 className="font-heading mt-1 text-2xl font-semibold tracking-tight">
          Tableau de bord
        </h2>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="kpi-ca-potentiel"
        >
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[hsl(var(--accent-gold))]" />
            <span className="text-label text-muted-foreground">
              CA potentiel actif
            </span>
          </div>
          <div className="font-heading mt-2 text-3xl font-semibold tracking-tight">
            {formatFCFA(ca?.active || 0)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {ca?.parsed_count || 0} leads avec budget renseigné
          </div>
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="kpi-ca-won"
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <span className="text-label text-muted-foreground">CA gagné</span>
          </div>
          <div className="font-heading mt-2 text-3xl font-semibold tracking-tight">
            {formatFCFA(ca?.won || 0)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Ventes conclues cumulées
          </div>
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="kpi-programmes"
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[hsl(var(--accent-navy))]" />
            <span className="text-label text-muted-foreground">Programmes actifs</span>
          </div>
          <div className="font-heading mt-2 text-3xl font-semibold tracking-tight">
            {byProgramme.filter((p) => p.programme !== "Non assigné").length}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {byProgramme.reduce((s, p) => s + p.total, 0)} leads répartis
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="chart-weekly"
        >
          <div className="mb-3">
            <div className="text-label text-muted-foreground">
              Évolution hebdomadaire
            </div>
            <h3 className="font-heading mt-0.5 text-base font-semibold">
              Leads créés par semaine
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              {/* Brighter cursor so the user sees exactly which week is being
                  read — empty bars have 0 height (invisible) so the tooltip
                  used to feel like "wrong number on the tallest bar". */}
              <Tooltip
                content={<TooltipBox />}
                cursor={{ fill: "hsl(var(--accent-navy))", opacity: 0.12 }}
              />
              <Bar dataKey="count" name="Leads" fill="hsl(var(--accent-navy))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="chart-buckets"
        >
          <div className="mb-3">
            <div className="text-label text-muted-foreground">Répartition</div>
            <h3 className="font-heading mt-0.5 text-base font-semibold">
              Par classement (HOT / INTÉRESSÉ / NURTURE / ...)
            </h3>
          </div>
          {pieData.length === 0 ? (
            <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
              Aucune donnée
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((d) => (
                    <Cell key={d.key} fill={BUCKET_COLORS[d.key]} />
                  ))}
                </Pie>
                <Tooltip content={<TooltipBox />} />
                <Legend
                  iconSize={8}
                  formatter={(v) => <span className="text-xs">{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Conversion by programme */}
      {byProgramme.length > 0 && (
        <div
          className="rounded-xl border border-border/60 bg-card p-5"
          data-testid="chart-conversion"
        >
          <div className="mb-3">
            <div className="text-label text-muted-foreground">Conversion</div>
            <h3 className="font-heading mt-0.5 text-base font-semibold">
              Taux de conversion par programme
            </h3>
          </div>
          <div className="space-y-2">
            {byProgramme.map((p) => (
              <div key={p.programme} className="flex items-center gap-3 text-sm">
                <div className="w-40 truncate font-medium">{p.programme}</div>
                <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-muted">
                  <div
                    className="h-full rounded-md bg-emerald-500"
                    style={{ width: `${Math.min(100, p.taux_conversion)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-2 text-xs font-semibold">
                    <span>{p.gagne}</span>
                    <span className="ml-1 text-muted-foreground">
                      / {p.total} · {p.taux_conversion}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
